import React, { useState, useEffect } from 'react';
import classNameBind from 'classnames/bind';
import _flatten from 'lodash/flatten';
import ActionList from '../../../ActionList';
import Button from '../../../Button';
import Checkbox from '../../../Checkbox';
import Icon from '../../../Icon';
import Popover from '../../../Popover';
import Text from '../../../Text';
import Tooltip from '../../../Tooltip';
import uuid from '../../../../utils/methods/uuid';
import styles from './Folder.module.css';

const bindClasses = classNameBind.bind(styles);

const Folder = ({ id = uuid(), name, addPopover, checkbox, actionsPopover, children, isOpenForced, nestingLvl = 0, isFavorite }) => {
  const [isOpen, setIsOpen] = useState(isOpenForced);
  const [popoverOpened, setPopoverOpened] = useState(false);
  const isFolderEmpty = !children || (Array.isArray(children) && !children.length);

  useEffect(() => {
    if (isOpenForced) setIsOpen(isOpenForced);
  }, [isOpenForced]);

  const isActive = React.useMemo(() => {
    const checkChildrenFolders = (childrenOfFolder) => {
      if (!childrenOfFolder || !childrenOfFolder.length || !childrenOfFolder[0]) return false;

      if (childrenOfFolder.findIndex(el => el.type?.displayName === 'Sidebar.Element' && el.props.isActive) > -1) {
        return true;
      }

      return checkChildrenFolders(
        _flatten(
          childrenOfFolder
            .filter(el => el.type?.displayName === 'Sidebar.Folder')
            .map(el => el.props.children),
        ),
      );
    };

    return checkChildrenFolders(Array.isArray(children) ? children : [children]);
  }, [children]);

  const passNestedLvl = (child) => {
    if (child.type && ['Sidebar.Folder', 'Sidebar.Element'].includes(child.type.displayName)) {
      return {
        ...child,
        props: {
          ...child.props,
          nestingLvl: nestingLvl + 1,
        },
      };
    }

    return child;
  };

  return (
    <>
      <div
        className={bindClasses('wrapper', { active: isActive, hover: popoverOpened, empty: isFolderEmpty })}
        data-ppuc-id="Sidebar.Folder"
      >
        {checkbox && (
          <div className={bindClasses('checkbox-wrapper', { selected: checkbox.checked })}>
            <Checkbox
              name={`checkbox_${id}`}
              id={`checkbox_${id}`}
              dataId={`Sidebar.Folder.Checkbox_${id}`}
              checked={checkbox.checked}
              onChange={() => {
                checkbox.onSelect(id);
              }}
              checkedMixed={checkbox.checkedMixed}
            />
          </div>
        )}
        <div
          className={bindClasses('flex-text')}
          style={{ paddingLeft: `${nestingLvl * 24}px` }}
          onClick={() => { setIsOpen(!isOpen); }}
          role="button"
          tabIndex={0}
        >
          <Tooltip content={name} position="top-start" hideSpeechBubble>
            <div className={bindClasses('element-main')}>
              <Icon
                name={isOpen || isFolderEmpty ? 'folder-opened' : 'folder-closed'}
                size="default"
                className={bindClasses('element-main-icon', 'folder')}
              />
              <Text dataId="Sidebar.Folder.Name">
                {name}
              </Text>
            </div>
          </Tooltip>
        </div>
        {isFavorite && (
          <div className={bindClasses('favorite-icon')} data-ppuc-id="Sidebar.Folder.Favorite">
            <Icon name="star" color="yellow" />
          </div>
        )}
        {addPopover && (
          <Popover
            position="bottom"
            onOpen={() => setPopoverOpened(true)}
            onClose={() => setPopoverOpened(false)}
            activatorElement={
              (
                <div className={bindClasses('options-button')}>
                  <Button
                    icon="plus"
                    onClick={() => {}}
                    appearance="subdued"
                    dataId="Sidebar.Folder.AddButton"
                    size="small"
                  />
                </div>
              )
            }
            subdued={false}
            hoverTrigger={false}
            noPadding
          >
            {close => (
              <Popover.Section padding="narrow">
                <ActionList
                  dataId="Sidebar.Folder.Add"
                  groups={addPopover.groups}
                  onItemSelected={(event) => {
                    addPopover.onItemSelected(event);
                    close();
                  }}
                />
              </Popover.Section>
            )}
          </Popover>
        )}
        {actionsPopover && (
          <Popover
            position="bottom"
            onOpen={() => setPopoverOpened(true)}
            onClose={() => setPopoverOpened(false)}
            activatorElement={
              (
                <div className={bindClasses('options-button')}>
                  <Button
                    icon="actions"
                    onClick={() => {}}
                    appearance="subdued"
                    dataId="Sidebar.Folder.ActionsButton"
                    size="small"
                  />
                </div>
              )
            }
            subdued={false}
            hoverTrigger={false}
            noPadding
          >
            {close => (
              <Popover.Section padding="narrow">
                <ActionList
                  dataId="Sidebar.Folder.Actions"
                  groups={actionsPopover.groups}
                  onItemSelected={(event) => {
                    actionsPopover.onItemSelected(event);
                    close();
                  }}
                />
              </Popover.Section>
            )}
          </Popover>
        )}
      </div>
      {
        isOpen && !isFolderEmpty && (
          (children?.length || 0) > 0 ? children.map(passNestedLvl) : passNestedLvl(children)
        )
      }
    </>
  );
};

Folder.displayName = 'Sidebar.Folder';

export default Folder;
